import {observable, transaction} from 'mobx';
import moment from 'moment';
import * as TableAPI from '../apis/TableAPI';
import * as DashboardAPI from '../apis/DashboardAPI';
import {AnalyticsTab, Store} from './store';
import {TopTenSearchtermRange} from '../types/Dashboard';
import {BarChartData} from '../views/charts/BarChart';
import {FFDropdownListItem} from '../views/FFDropdownList';
import {DateRange, TimeState} from './TimeIntervalStore';
import {AxiosResponse} from 'axios';
import {DateUtils} from '../utility/DateUtils';
import {KpiDataPackage, KpiPackage} from '../types/Chart';
import {MultiSearchEntry} from '../types/api/dashboard/DashboardTopSearchterms';
import {TableSortDirection} from '../types/Table';
import {KPI, KPISpecialCases, LongtailKPI, ProductKPI, SearchtermKPI, SearchtermSlopeOutlierKPI} from '../kpi';
import * as SearchtermAPI from '../apis/SearchtermAPI';
import {MessageProvider} from '../i18n/i18n';
import {Channel} from '../types/Channel';
import {ChannelState, ChannelStatus} from './ChannelStore';
import DataVersionChecker, {DataVersionInfo} from '../utility/DataVersionChecker';
import {TopFilterData} from '../types/api/dashboard/DashboardFilters';
import {TimeSeriesEntry} from '../apis/TimeSeriesEntry';
import {TableDataForChannelResponse} from '../types/api/tableData/TableDataForChannel';

export enum DiagramType {
    TOP_TEN_SEARCHTERMS = 'top10Searchterms',
    TOP_TEN_SEARCHTERMS_YESTERDAY = 'top10SearchtermsYesterday',
    SALES = 'sales',
    SEARCHES = 'searches',
    FLOP_TEN_SEARCHTERMS = 'flopTenSearchterms',
    HOT_TEN_SEARCHTERMS = 'hotTenSearchterms',
}

export interface OverviewStatus {
    loading: boolean;
    data: {
        salesPerSearch: number;
        searchCount: number;
        searchTime: number;
        totalSales: number;
        salesFromSearches: number;
        salesFromReco: number;
        salesPerSearchCompare: number;
        searchCountCompare: number;
        searchTimeCompare: number;
        totalSalesCompare: number;
        salesFromSearchesCompare: number;
        salesFromRecoCompare: number;
    };
}

type LongtailSelectionRanges = {[key in LongtailKPI]: number};
type LongtailStatus = {[key in LongtailKPI]: DashboardDiagramStatus};

interface DashboardDiagramStatus {
    isLoading: boolean;
    data: BarChartData[];
}

const LOADING_EMPTY_DIAGRAM_STATUS: DashboardDiagramStatus = {
    isLoading: true,
    data: [],
};

const LOADING_FAILED_NO_DATA_STATUS: DashboardDiagramStatus = {
    isLoading: false,
    data: [],
};

const LOADING_EMPTY_OVERVIEW_STATUS: OverviewStatus = {
    loading: true,
    data: {
        salesPerSearch: 0,
        salesPerSearchCompare: 0,
        totalSales: 0,
        salesFromSearches: 0,
        salesFromReco: 0,
        totalSalesCompare: 0,
        searchCount: 0,
        searchCountCompare: 0,
        searchTime: 0,
        searchTimeCompare: 0,
        salesFromSearchesCompare: 0,
        salesFromRecoCompare: 0,
    },
};

interface DashboardData {
    overview: OverviewStatus;
    topTenSearchtermsYesterday: DashboardDiagramStatus;
    topTenSearchterms: DashboardDiagramStatus;
    dailySales: DashboardDiagramStatus;
    dailySearches: DashboardDiagramStatus;
    flopTenSearchterms: DashboardDiagramStatus;
    hotTenSearchterms: DashboardDiagramStatus;
    multiWordSearchCountAndAvgLoadingTimes: DashboardDiagramStatus;
    longtailStatus: LongtailStatus;
    longtailSelectionRanges: LongtailSelectionRanges;
    filterTableData: FilterTableStatus;
}

export interface FilterTableStatus {
    isLoading: boolean;
    data: TopFilterData;
}

class DashboardStore {
    private dataVersionChecker: DataVersionChecker;
    private topFilterDataVersionChecker: DataVersionChecker;
    @observable public overviewFoldoutIsVisible = false;

    private dashboardData = observable.map<string, DashboardData>({});

    constructor(
        private readonly mainStore: Store,
        private t: MessageProvider,
        public readonly channelState: ChannelState,
        public readonly timeState: TimeState
    ) {
        this.dataVersionChecker = new DataVersionChecker();
        this.topFilterDataVersionChecker = new DataVersionChecker();
    }

    public loadFoldoutTableData = () => {
        const {selectedChannel} = this.channelState;
        const {mainDateRange} = this.timeState;

        if (!selectedChannel) {
            return;
        }

        this.loadTopFilter(mainDateRange, selectedChannel);
    };

    private loadTopFilter(mainDateRange: DateRange, channel: Channel) {
        const topFilterVersionInfo = {
            mainDateRange,
            channel,
        };

        if (!this.topFilterDataVersionChecker.checkIfDataVersionAlreadyExists(topFilterVersionInfo)) {
            this.resetOverviewTableFilter(channel);
            this.topFilterDataVersionChecker.addDataVersionInfo(topFilterVersionInfo);
            DashboardAPI.getTopFilters({
                limit: 10000,
                channel: channel.name,
                interval: mainDateRange,
            })
                .then((resp) => {
                    if (this.checkIfTableFilterDataIsInvalid(channel, mainDateRange)) {
                        return;
                    }
                    this.getDashboardData(channel.name).filterTableData = {
                        ...this.getDashboardData(channel.name).filterTableData,
                        data: resp.data,
                        isLoading: false,
                    };
                })
                .catch(() => {
                    if (this.checkIfTableFilterDataIsInvalid(channel, mainDateRange)) {
                        return;
                    }
                    this.getDashboardData(channel.name).filterTableData = {
                        ...this.getDashboardData(channel.name).filterTableData,
                        data: this.getLoadingDashbordDataStructure().filterTableData.data,
                        isLoading: false,
                    };
                });
        }
    }

    private checkIfTableFilterDataIsInvalid(channel: Channel, mainDateRange: DateRange) {
        return !this.topFilterDataVersionChecker.checkIfDataVersionAlreadyExists({mainDateRange, channel});
    }

    private getDashboardData(channelName: string): DashboardData {
        return this.dashboardData.get(channelName)!;
    }

    public setLongtailLimitAndReloadDiagram(kpi: LongtailKPI, limit: number) {
        const {selectedChannel} = this.channelState;
        const {mainDateRange, compareDateRange} = this.timeState;

        if (selectedChannel) {
            const data = this.getDashboardData(selectedChannel.name);
            data.longtailSelectionRanges[kpi] = limit;
            if (!data.longtailStatus[kpi]) {
                throw Error(`KPI ${kpi} not supported for longtail diagrams`);
            }
            data.longtailStatus[kpi] = LOADING_EMPTY_DIAGRAM_STATUS;
            this.loadLongtailDataForKpi(kpi, selectedChannel, mainDateRange, compareDateRange);
        }
    }

    public changeDate = (dateRange: DateRange): void => {
        const {startDate, endDate} = dateRange;
        this.timeState.setMainDateRange({
            startDate: startDate ? startDate.startOf('day') : undefined,
            endDate: endDate ? endDate.endOf('day') : undefined,
        });
    };

    public setSelectedChannel(selectedChannel: Channel | string): void {
        this.channelState.setSelectedChannel(selectedChannel);
    }

    public getActiveChannelsDropdownFormat(noAvailableChannelsText: string): FFDropdownListItem[] {
        const {availableChannels, channelLoadingStatus} = this.channelState;
        if (channelLoadingStatus === ChannelStatus.Loading) {
            return [{value: '', text: ''}];
        } else {
            return availableChannels.length !== 0 ? this.convertToDropdownFormat(availableChannels) : [{value: '', text: noAvailableChannelsText}];
        }
    }

    private convertToDropdownFormat(channels: Channel[]): FFDropdownListItem[] {
        return channels.map((data) => {
            return {value: data.name, text: data.name};
        });
    }

    private getCurrentDataVersionInfo(): DataVersionInfo {
        const {selectedChannel} = this.channelState;
        const {mainDateRange, compareDateRange} = this.timeState;
        return {
            channel: selectedChannel,
            mainDateRange: DateUtils.copyDateRange(mainDateRange),
            compareDateRange: DateUtils.copyDateRange(compareDateRange),
        };
    }

    private getLoadingDashbordDataStructure(): DashboardData {
        return {
            overview: LOADING_EMPTY_OVERVIEW_STATUS,
            topTenSearchtermsYesterday: LOADING_EMPTY_DIAGRAM_STATUS,
            topTenSearchterms: LOADING_EMPTY_DIAGRAM_STATUS,
            dailySales: LOADING_EMPTY_DIAGRAM_STATUS,
            dailySearches: LOADING_EMPTY_DIAGRAM_STATUS,
            flopTenSearchterms: LOADING_EMPTY_DIAGRAM_STATUS,
            hotTenSearchterms: LOADING_EMPTY_DIAGRAM_STATUS,
            multiWordSearchCountAndAvgLoadingTimes: LOADING_EMPTY_DIAGRAM_STATUS,
            longtailStatus: {
                [SearchtermKPI.SearchCount]: LOADING_EMPTY_DIAGRAM_STATUS,
                [SearchtermKPI.ClickCount]: LOADING_EMPTY_DIAGRAM_STATUS,
                [SearchtermKPI.CartCount]: LOADING_EMPTY_DIAGRAM_STATUS,
                [SearchtermKPI.CheckoutCount]: LOADING_EMPTY_DIAGRAM_STATUS,
            },
            longtailSelectionRanges: {
                [SearchtermKPI.SearchCount]: 1000,
                [SearchtermKPI.ClickCount]: 1000,
                [SearchtermKPI.CartCount]: 1000,
                [SearchtermKPI.CheckoutCount]: 1000,
            },
            filterTableData: {
                data: {
                    asn: {
                        filterValues: [],
                        filterNames: [],
                    },
                    navigation: {
                        filterNames: [],
                        filterValues: [],
                    },
                    searchFilterDepth: 0,
                    searchFilterUsageRatio: 0,
                },
                isLoading: true,
            },
        };
    }

    private resetDataForChannel(channel: Channel) {
        this.dashboardData.set(channel.name, this.getLoadingDashbordDataStructure());
    }

    private resetOverviewTableFilter(channel: Channel) {
        const defaultData = this.getLoadingDashbordDataStructure();
        if (this.dashboardData.has(channel.name)) {
            this.getDashboardData(channel.name).filterTableData = defaultData.filterTableData;
        } else {
            this.dashboardData.set(channel.name, defaultData);
        }
    }

    public loadTableAndDiagramData(): void {
        if (!this.dataVersionChecker.checkIfDataVersionAlreadyExists(this.getCurrentDataVersionInfo())) {
            const {mainDateRange, compareDateRange} = this.timeState;
            const {selectedChannel} = this.channelState;
            if (!selectedChannel) {
                return;
            }
            this.resetDataForChannel(selectedChannel);
            this.dataVersionChecker.addDataVersionInfo(this.getCurrentDataVersionInfo());
            this.loadTableData(selectedChannel, mainDateRange, compareDateRange);
            this.loadDiagramData(selectedChannel, mainDateRange, compareDateRange);
        }
    }

    /**
     * Loads table data separate from each other for different time ranges
     */
    private loadTableData(selectedChannel: Channel, mainDateRange: DateRange, compareDateRange: DateRange): void {
        DashboardAPI.getDashboardTableData(
            {
                interval: DateUtils.toDailyDateRange(mainDateRange),
                channel: selectedChannel.name,
            },
            {
                interval: DateUtils.toDailyDateRange(compareDateRange),
                channel: selectedChannel.name,
            }
        )
            .then(([currentResp, compareResp]) => {
                if (this.checkIfLoadedDataIsInvalid(selectedChannel, mainDateRange, compareDateRange)) {
                    return;
                }

                this.getDashboardData(selectedChannel.name).overview = {
                    loading: false,
                    data: {
                        salesPerSearch: currentResp.data.sales / currentResp.data.searchCount,
                        searchCount: currentResp.data.searchCount,
                        searchTime: currentResp.data.avgSearchTime,
                        totalSales: currentResp.data.sales,
                        salesFromSearches: currentResp.data.salesFromSearches,
                        salesFromReco: currentResp.data.salesFromReco,
                        salesPerSearchCompare: compareResp.data.sales / compareResp.data.searchCount,
                        searchCountCompare: compareResp.data.searchCount,
                        searchTimeCompare: compareResp.data.avgSearchTime,
                        totalSalesCompare: compareResp.data.sales,
                        salesFromSearchesCompare: compareResp.data.salesFromSearches,
                        salesFromRecoCompare: compareResp.data.salesFromReco,
                    },
                };
            })
            .catch(() => {
                if (this.checkIfLoadedDataIsInvalid(selectedChannel, mainDateRange, compareDateRange)) {
                    return;
                }
                this.getDashboardData(selectedChannel.name).overview.loading = false;
            });
    }

    private checkIfLoadedDataIsInvalid(channel: Channel, mainDateRange: DateRange, compareDateRange: DateRange): boolean {
        return !this.dataVersionChecker.checkIfDataVersionAlreadyExists({channel, mainDateRange, compareDateRange});
    }

    private formatMultiWordSearchCountAndAvgLoadingTimesDataToBarChartData = (
        wordCountData: MultiSearchEntry,
        selectedChannel: Channel
    ): BarChartData => {
        const wordName = `${wordCountData.wordCount} ${this.t('dashboard:word', {count: wordCountData.wordCount})}`;
        return {
            name: wordName,
            data: [
                this.generateKpiDataPackageFiller(SearchtermKPI.SearchCount, wordCountData.searchCount, selectedChannel, false, {
                    name: wordName,
                }),
                this.generateKpiDataPackageFiller(KPISpecialCases.AvgSearchTime, Math.round(wordCountData.avgSearchTime), selectedChannel, false, {
                    name: wordName,
                }),
            ],
        };
    };

    private loadMultiSearchtermData(selectedChannel: Channel, mainDateRange: DateRange, compareDateRange: DateRange): void {
        DashboardAPI.multiWordSearchesPerChannel({
            interval: DateUtils.toDailyDateRange(mainDateRange),
            channel: selectedChannel.name,
        })
            .then((resp) => {
                if (this.checkIfLoadedDataIsInvalid(selectedChannel, mainDateRange, compareDateRange)) {
                    return;
                }
                const topTenWordSearchResult = resp.data.wordSearchResult.filter(
                    (wordResult: MultiSearchEntry) => wordResult.wordCount > 0 && wordResult.wordCount <= 10
                );
                this.getDashboardData(selectedChannel.name).multiWordSearchCountAndAvgLoadingTimes = {
                    data: topTenWordSearchResult.map((wordCountData: MultiSearchEntry) =>
                        this.formatMultiWordSearchCountAndAvgLoadingTimesDataToBarChartData(wordCountData, selectedChannel)
                    ),
                    isLoading: false,
                };
            })
            .catch(() => {
                if (this.checkIfLoadedDataIsInvalid(selectedChannel, mainDateRange, compareDateRange)) {
                    return;
                }
                this.getDashboardData(selectedChannel.name).multiWordSearchCountAndAvgLoadingTimes = LOADING_FAILED_NO_DATA_STATUS;
            });
    }

    private loadTrendingSearchterms(
        sortAttribute: KPI,
        sortDirection: TableSortDirection,
        setLoading: (loading: boolean) => void,
        success: (chartData: BarChartData[]) => void,
        selectedChannel: Channel,
        mainDateRange: DateRange,
        compareDateRange: DateRange
    ): void {
        if (!selectedChannel) {
            setLoading(false);
            return;
        }

        TableAPI.tableDataForChannel({
            channel: selectedChannel.name,
            mode: 'searchterm',
            interval: DateUtils.toDailyDateRange(mainDateRange),
            search: '',
            sorting: {
                attribute: sortAttribute,
                sortDirection,
            },
            limit: 10,
            filter: JSON.stringify([]),
        })
            .then((resp) => {
                if (this.checkIfLoadedDataIsInvalid(selectedChannel, mainDateRange, compareDateRange)) {
                    return;
                }
                SearchtermAPI.getKpiCountForSearchterms({
                    channel: selectedChannel.name,
                    kpi: SearchtermKPI.SearchCount,
                    interval: DateUtils.toDailyDateRange(compareDateRange),
                    searchterms: resp.data.map((data) => data.searchterm || ''),
                })
                    .then((respCompare) => {
                        if (this.checkIfLoadedDataIsInvalid(selectedChannel, mainDateRange, compareDateRange)) {
                            return;
                        }
                        success(
                            resp.data.map(
                                (tableData): BarChartData => ({
                                    name: tableData.searchterm || '',
                                    data: [
                                        this.generateKpiDataPackageFiller(SearchtermKPI.SearchCount, tableData.searchCount || 0, selectedChannel),
                                        this.generateKpiDataPackageFiller(
                                            SearchtermKPI.SearchCount,
                                            this.getCompareCountFromCompareSearchtermCounts(respCompare.data, tableData.searchterm || ''),
                                            selectedChannel,
                                            true
                                        ),
                                    ],
                                })
                            )
                        );
                        setLoading(false);
                    })
                    .catch(() => {
                        setLoading(false);
                    });
            })
            .catch(() => {
                if (this.checkIfLoadedDataIsInvalid(selectedChannel, mainDateRange, compareDateRange)) {
                    return;
                }
                setLoading(false);
            });
    }

    private getCompareCountFromCompareSearchtermCounts(compareSearchtermCounts: SearchtermAPI.KpiCountForSearchtermsResponse[], searchterm: string) {
        const matchingCompareCount = compareSearchtermCounts.find((compareSearchterm) => compareSearchterm.query === searchterm);
        if (matchingCompareCount) {
            return matchingCompareCount.count;
        }
        return 0;
    }

    private loadTopTenSearchtermsDiagramData(selectedChannel: Channel, mainDateRange: DateRange, compareDateRange: DateRange): void {
        DashboardAPI.getDashboardTopTenSearchtermData({
            interval: DateUtils.toHourlyDateRange(mainDateRange),
            compareInterval: DateUtils.toHourlyDateRange(compareDateRange),
            channel: selectedChannel.name,
        })
            .then((resp) => {
                if (this.checkIfLoadedDataIsInvalid(selectedChannel, mainDateRange, compareDateRange)) {
                    return;
                }
                this.getDashboardData(selectedChannel.name).topTenSearchterms = {
                    isLoading: false,
                    data: resp.data.map((topSearchtermData) => {
                        return {
                            name: topSearchtermData.searchterm,
                            data: [
                                this.generateKpiDataPackageFiller(SearchtermKPI.SearchCount, topSearchtermData.count, selectedChannel),
                                this.generateKpiDataPackageFiller(SearchtermKPI.SearchCount, topSearchtermData.compareCount, selectedChannel, true),
                            ],
                        };
                    }),
                };
            })
            .catch(() => {
                if (this.checkIfLoadedDataIsInvalid(selectedChannel, mainDateRange, compareDateRange)) {
                    return;
                }
                this.getDashboardData(selectedChannel.name).topTenSearchterms = LOADING_FAILED_NO_DATA_STATUS;
            });
    }

    private loadTopTenSearchtermsYesterdayDiagramData(selectedChannel: Channel, mainDateRange: DateRange, compareDateRange: DateRange): void {
        DashboardAPI.getDashboardTopTenSearchtermData({
            interval: DateUtils.toHourlyDateRange({
                startDate: moment()
                    .subtract(2, 'days')
                    .startOf('day'),
                endDate: moment()
                    .subtract(1, 'days')
                    .startOf('day'),
            }),
            compareInterval: DateUtils.toHourlyDateRange({
                startDate: moment()
                    .subtract(2, 'days')
                    .startOf('day'),
                endDate: moment()
                    .subtract(1, 'days')
                    .startOf('day'),
            }),
            channel: selectedChannel.name,
        })
            .then((resp) => {
                if (this.checkIfLoadedDataIsInvalid(selectedChannel, mainDateRange, compareDateRange)) {
                    return;
                }
                this.getDashboardData(selectedChannel.name).topTenSearchtermsYesterday = {
                    isLoading: false,
                    data: resp.data.map((topSearchtermData) => ({
                        name: topSearchtermData.searchterm,
                        data: [this.generateKpiDataPackageFiller(SearchtermKPI.SearchCount, topSearchtermData.count, selectedChannel)],
                    })),
                };
            })
            .catch(() => {
                if (this.checkIfLoadedDataIsInvalid(selectedChannel, mainDateRange, compareDateRange)) {
                    return;
                }
                this.getDashboardData(selectedChannel.name).topTenSearchtermsYesterday = LOADING_FAILED_NO_DATA_STATUS;
            });
    }

    private loadDiagramData(selectedChannel: Channel, mainDateRange: DateRange, compareDateRange: DateRange): void {
        this.loadTopTenSearchtermsDiagramData(selectedChannel, mainDateRange, compareDateRange);
        this.loadTopTenSearchtermsYesterdayDiagramData(selectedChannel, mainDateRange, compareDateRange);
        this.loadDailySearchesDiagramData(selectedChannel, mainDateRange, compareDateRange);
        this.loadDailySalesDiagramData(selectedChannel, mainDateRange, compareDateRange);
        this.loadMultiSearchtermData(selectedChannel, mainDateRange, compareDateRange);

        if (!this.mainStore.skipSlopes) {
            this.loadTrendingSearchterms(
                SearchtermSlopeOutlierKPI.MaxSearchSlopeValue,
                TableSortDirection.DESC,
                (isLoading: boolean) => {
                    this.getDashboardData(selectedChannel.name).flopTenSearchterms.isLoading = isLoading;
                },
                (chartData: BarChartData[]) => {
                    this.getDashboardData(selectedChannel.name).flopTenSearchterms.data = chartData;
                },
                selectedChannel,
                mainDateRange,
                compareDateRange
            );
            this.loadTrendingSearchterms(
                SearchtermSlopeOutlierKPI.MinSearchSlopeValue,
                TableSortDirection.ASC,
                (isLoading: boolean) => {
                    this.getDashboardData(selectedChannel.name).hotTenSearchterms.isLoading = isLoading;
                },
                (chartData: BarChartData[]) => {
                    this.getDashboardData(selectedChannel.name).hotTenSearchterms.data = chartData;
                },
                selectedChannel,
                mainDateRange,
                compareDateRange
            );
        }

        this.loadLongtailDataForKpi(SearchtermKPI.SearchCount, selectedChannel, mainDateRange, compareDateRange);
        this.loadLongtailDataForKpi(SearchtermKPI.ClickCount, selectedChannel, mainDateRange, compareDateRange);
        this.loadLongtailDataForKpi(SearchtermKPI.CartCount, selectedChannel, mainDateRange, compareDateRange);
        this.loadLongtailDataForKpi(SearchtermKPI.CheckoutCount, selectedChannel, mainDateRange, compareDateRange);
    }

    private loadLongtailDataForKpi(kpi: LongtailKPI, selectedChannel: Channel, mainDateRange: DateRange, compareDateRange: DateRange): void {
        const data = this.getDashboardData(selectedChannel.name);
        if (!data.longtailStatus[kpi]) {
            throw Error(`KPI ${kpi} not supported for longtail diagrams`);
        }

        this.loadLongtailData(
            kpi,
            (isLoading: boolean) => {
                data.longtailStatus[kpi].isLoading = isLoading;
            },
            (barChartData: BarChartData[]) => {
                data.longtailStatus[kpi].data = barChartData;
            },
            selectedChannel,
            mainDateRange,
            compareDateRange
        );
    }

    private groupSortAndAddMissingDailyEntries(
        mainResp: AxiosResponse<TimeSeriesEntry[]>,
        compareResp: AxiosResponse<TimeSeriesEntry[]>,
        kpi: SearchtermKPI,
        selectedChannel: Channel
    ): BarChartData[] {
        const {mainDateRange, compareDateRange} = this.timeState;

        const dailySalesDataMain = mainResp.data.map((dailyMain) => {
            const momentDate = moment(dailyMain.timestamp, 'x').startOf('day');
            return {
                momentDate,
                date: momentDate.format(DateUtils.getDayMonthFormat()),
                main: dailyMain.count,
            };
        });

        const dailySalesDataCompare = compareResp.data.map((dailyCompare) => {
            const offset =
                mainDateRange.startDate && compareDateRange.startDate ? mainDateRange.startDate.unix() - compareDateRange.startDate.unix() : 0;
            const momentDate = moment(dailyCompare.timestamp + offset, 'x');
            return {
                momentDate,
                date: momentDate.format(DateUtils.getDayMonthFormat()),
                compare: dailyCompare.count,
            };
        });

        return dailySalesDataMain.map((dailySalesMain, index) => {
            return {
                name: dailySalesMain.date,
                date: dailySalesMain.momentDate,
                data: [
                    this.generateKpiDataPackageFiller(kpi, dailySalesMain.main || 0, selectedChannel),
                    this.generateKpiDataPackageFiller(kpi, dailySalesDataCompare[index].compare || 0, selectedChannel, true),
                ],
            };
        });
    }

    private loadLongtailData(
        kpi: LongtailKPI,
        setLoading: (loading: boolean) => void,
        success: (chartData: BarChartData[]) => void,
        selectedChannel: Channel,
        mainDateRange: DateRange,
        compareDateRange: DateRange
    ) {
        const limit = this.getDashboardData(selectedChannel.name).longtailSelectionRanges[kpi] || 1000;
        let modValue = 20;
        if (limit === 500) {
            modValue = 10;
        } else if (limit === 100) {
            modValue = 2;
        }

        if (!selectedChannel) {
            setLoading(false);
            return;
        }

        Promise.all([
            TableAPI.tableDataForChannel({
                mode: 'searchterm',
                channel: selectedChannel.name,
                interval: DateUtils.toDailyDateRange(mainDateRange),
                search: '',
                limit,
                filter: JSON.stringify([]),
                sorting: {sortDirection: TableSortDirection.DESC, attribute: kpi},
            }),
            SearchtermAPI.getKpiSum({
                channel: selectedChannel.name,
                interval: DateUtils.toDailyDateRange(mainDateRange),
                kpi,
            }),
        ])
            .then(([tableDataResp, kpiSumResp]: [AxiosResponse<TableDataForChannelResponse[]>, AxiosResponse<number>]) => {
                if (this.checkIfLoadedDataIsInvalid(selectedChannel, mainDateRange, compareDateRange)) {
                    return;
                }
                const tableData = tableDataResp.data;
                const kpiSum = kpiSumResp.data;
                let sumPredecessors = 0;
                const searchClickCartCheckoutLongtailLoading: BarChartData[] = [];

                tableData.forEach((singleRowData, index) => {
                    sumPredecessors += singleRowData[kpi]!;
                    if (index % modValue === 0) {
                        searchClickCartCheckoutLongtailLoading.push({
                            name: index.toString(),
                            data: [
                                this.generateKpiDataPackageFiller(kpi, singleRowData[kpi]!, selectedChannel, false, {
                                    searchterm: singleRowData.searchterm,
                                    rank: index,
                                    percentage: kpiSum > 0 ? sumPredecessors / kpiSum : 0,
                                }),
                            ],
                        });
                    }
                });
                success(searchClickCartCheckoutLongtailLoading);
                setLoading(false);
            })
            // tslint:disable-next-line:no-identical-functions
            .catch(() => {
                if (this.checkIfLoadedDataIsInvalid(selectedChannel, mainDateRange, compareDateRange)) {
                    return;
                }
                setLoading(false);
            });
    }

    private loadDailySalesDiagramData(selectedChannel: Channel, mainDateRange: DateRange, compareDateRange: DateRange): void {
        Promise.all([
            SearchtermAPI.getDailySearchtermDataSummary({
                channel: selectedChannel.name,
                interval: DateUtils.toHourlyDateRange(mainDateRange),
                kpi: SearchtermKPI.SalesFromSearches,
            }),
            SearchtermAPI.getDailySearchtermDataSummary({
                channel: selectedChannel.name,
                interval: DateUtils.toHourlyDateRange(compareDateRange),
                kpi: SearchtermKPI.SalesFromSearches,
            }),
        ])
            .then(([mainResp, compareResp]: [AxiosResponse<TimeSeriesEntry[]>, AxiosResponse<TimeSeriesEntry[]>]) => {
                if (this.checkIfLoadedDataIsInvalid(selectedChannel, mainDateRange, compareDateRange)) {
                    return;
                }
                this.getDashboardData(selectedChannel.name).dailySales = {
                    isLoading: false,
                    data: this.groupSortAndAddMissingDailyEntries(mainResp, compareResp, SearchtermKPI.SalesFromSearches, selectedChannel),
                };
            })
            .catch(() => {
                if (this.checkIfLoadedDataIsInvalid(selectedChannel, mainDateRange, compareDateRange)) {
                    return;
                }
                this.getDashboardData(selectedChannel.name).dailySales = LOADING_FAILED_NO_DATA_STATUS;
            });
    }

    private loadDailySearchesDiagramData(selectedChannel: Channel, mainDateRange: DateRange, compareDateRange: DateRange): void {
        Promise.all([
            SearchtermAPI.getDailySearchtermDataSummary({
                channel: selectedChannel.name,
                interval: DateUtils.toHourlyDateRange(mainDateRange),
                kpi: SearchtermKPI.SearchCount,
            }),
            SearchtermAPI.getDailySearchtermDataSummary({
                channel: selectedChannel.name,
                interval: DateUtils.toHourlyDateRange(compareDateRange),
                kpi: SearchtermKPI.SearchCount,
            }),
        ])
            .then(([mainResp, compareResp]) => {
                if (this.checkIfLoadedDataIsInvalid(selectedChannel, mainDateRange, compareDateRange)) {
                    return;
                }

                this.getDashboardData(selectedChannel.name).dailySearches = {
                    data: this.groupSortAndAddMissingDailyEntries(mainResp, compareResp, SearchtermKPI.SearchCount, selectedChannel),
                    isLoading: false,
                };
            })
            .catch(() => {
                if (this.checkIfLoadedDataIsInvalid(selectedChannel, mainDateRange, compareDateRange)) {
                    return;
                }
                this.getDashboardData(selectedChannel.name).dailySearches = LOADING_FAILED_NO_DATA_STATUS;
            });
    }

    private openSearchtermViewTopTenSearchterms(barChartData: BarChartData, diagramType: DiagramType): void {
        const {selectedChannel} = this.channelState;
        if (!selectedChannel) {
            return;
        }

        const {name: searchterm} = barChartData;
        const {mainDateRange, setMainDateRangeWithoutReload} = this.timeState;

        this.mainStore.activeAnalyticsTab = AnalyticsTab.SearchView;
        const selectorTableStore = this.mainStore.selectorTableStore;
        const lineDiagramStore = this.mainStore.lineDiagramStore;

        transaction(() => {
            selectorTableStore.searchterm = searchterm;
            selectorTableStore.useExactSearching = true;
            selectorTableStore.tableSorting = {
                attribute: SearchtermKPI.SearchCount,
                sortDirection: TableSortDirection.DESC,
            };
            lineDiagramStore.termColorIndexes = [];
            lineDiagramStore.diagramData = [];
            lineDiagramStore.loadedDiagramData = [];
            lineDiagramStore.requestedChannelDiagrams = [];
            lineDiagramStore.updateTermColorIndexes(searchterm, SearchtermKPI.SearchCount, selectedChannel.name);
            lineDiagramStore.requestedNormalDiagrams = [
                {
                    searchtermOrProductId: searchterm,
                    channelName: selectedChannel.name,
                    kpi: SearchtermKPI.SearchCount,
                },
            ];

            switch (diagramType) {
                case DiagramType.TOP_TEN_SEARCHTERMS:
                    setMainDateRangeWithoutReload(mainDateRange);
                    this.mainStore.selectedDateRangeChanged(true);
                    break;
                case DiagramType.TOP_TEN_SEARCHTERMS_YESTERDAY:
                    setMainDateRangeWithoutReload({
                        startDate: moment()
                            .subtract(1, 'day')
                            .startOf('day'),
                        endDate: moment().startOf('day'),
                    });
                    this.mainStore.selectedDateRangeChanged(true);
                    break;
                default:
                    throw new Error('Invalid diagram type ' + diagramType);
            }
        });
    }

    public topTenSearchtermsYesterdayLabelClicked = (data: BarChartData): void => {
        this.openSearchtermViewTopTenSearchterms(data, DiagramType.TOP_TEN_SEARCHTERMS_YESTERDAY);
    };

    public topTenSearchtermsLastXDaysLabelClicked = (data: BarChartData): void => {
        this.openSearchtermViewTopTenSearchterms(data, DiagramType.TOP_TEN_SEARCHTERMS);
    };

    public searchCellClicked = (dateRange: string): void => {
        this.mainStore.activeAnalyticsTab = AnalyticsTab.SearchView;
        const {setMainDateRangeWithoutReload, mainDateRange, compareDateRange} = this.timeState;
        const selectorTableStore = this.mainStore.selectorTableStore;

        transaction(() => {
            selectorTableStore.searchterm = '';
            selectorTableStore.useExactSearching = false;
            selectorTableStore.tableSorting = {
                sortDirection: TableSortDirection.DESC,
                attribute: SearchtermKPI.SearchCount,
            };
            if (dateRange === 'main') {
                setMainDateRangeWithoutReload(mainDateRange);
            } else {
                setMainDateRangeWithoutReload(compareDateRange);
            }
            this.mainStore.selectedDateRangeChanged(true);
        });
    };

    public salesFromSearchesClicked = (dateRange: TopTenSearchtermRange): void => {
        this.mainStore.activeAnalyticsTab = AnalyticsTab.SearchView;
        const {setMainDateRangeWithoutReload, mainDateRange, compareDateRange} = this.timeState;
        const selectorTableStore = this.mainStore.selectorTableStore;

        transaction(() => {
            selectorTableStore.searchterm = '';
            selectorTableStore.useExactSearching = false;
            selectorTableStore.tableSorting = {
                sortDirection: TableSortDirection.DESC,
                attribute: SearchtermKPI.SalesFromSearches,
            };
            if (dateRange === TopTenSearchtermRange.MAIN) {
                setMainDateRangeWithoutReload(mainDateRange);
            } else {
                setMainDateRangeWithoutReload(compareDateRange);
            }
            this.mainStore.selectedDateRangeChanged(true);
        });
    };

    public salesClicked = (dateRange: TopTenSearchtermRange): void => {
        this.mainStore.activeAnalyticsTab = AnalyticsTab.ProductView;
        const {setMainDateRangeWithoutReload, mainDateRange, compareDateRange} = this.timeState;
        const selectorTableStore = this.mainStore.selectorTableStore;

        transaction(() => {
            selectorTableStore.searchterm = '';
            selectorTableStore.useExactSearching = false;
            selectorTableStore.tableSorting = {
                sortDirection: TableSortDirection.DESC,
                attribute: ProductKPI.Sales,
            };
            if (dateRange === TopTenSearchtermRange.MAIN) {
                setMainDateRangeWithoutReload(mainDateRange);
            } else {
                setMainDateRangeWithoutReload(compareDateRange);
            }
            this.mainStore.selectedDateRangeChanged(true);
        });
    };

    public avgSalesPerSearchClicked = (dateRange: TopTenSearchtermRange): void => {
        this.mainStore.activeAnalyticsTab = AnalyticsTab.SearchView;
        const {setMainDateRangeWithoutReload, mainDateRange, compareDateRange} = this.timeState;
        const selectorTableStore = this.mainStore.selectorTableStore;

        transaction(() => {
            selectorTableStore.searchterm = '';
            selectorTableStore.useExactSearching = false;
            selectorTableStore.tableSorting = {
                sortDirection: TableSortDirection.DESC,
                attribute: SearchtermKPI.SalesFromSearchesPerSearch,
            };

            if (dateRange === TopTenSearchtermRange.MAIN) {
                setMainDateRangeWithoutReload(mainDateRange);
            } else {
                setMainDateRangeWithoutReload(compareDateRange);
            }
            this.mainStore.selectedDateRangeChanged(true);
        });
    };

    public diagramTitleClicked(diagram: DiagramType): void {
        const {selectedChannel} = this.channelState;
        if (!selectedChannel) {
            return;
        }

        this.mainStore.activeAnalyticsTab = AnalyticsTab.SearchView;
        const {selectorTableStore, lineDiagramStore} = this.mainStore;
        const {mainDateRange, setMainDateRangeWithoutReload, setAllowDateCompareRangeWithoutReload} = this.timeState;

        transaction(() => {
            switch (diagram) {
                case DiagramType.TOP_TEN_SEARCHTERMS:
                    this.channelState.setSelectedChannel(selectedChannel);
                    selectorTableStore.searchterm = '';
                    selectorTableStore.useExactSearching = false;
                    selectorTableStore.tableSorting = {
                        attribute: SearchtermKPI.SearchCount,
                        sortDirection: TableSortDirection.DESC,
                    };
                    setMainDateRangeWithoutReload(mainDateRange);
                    this.mainStore.selectedDateRangeChanged(true);
                    break;
                case DiagramType.TOP_TEN_SEARCHTERMS_YESTERDAY:
                    this.channelState.setSelectedChannel(selectedChannel);
                    selectorTableStore.searchterm = '';
                    selectorTableStore.useExactSearching = false;
                    selectorTableStore.tableSorting = {
                        sortDirection: TableSortDirection.DESC,
                        attribute: SearchtermKPI.SearchCount,
                    };
                    setMainDateRangeWithoutReload({
                        startDate: moment()
                            .subtract(1, 'days')
                            .startOf('day'),
                        endDate: moment().startOf('day'),
                    });
                    this.mainStore.selectedDateRangeChanged(true);
                    break;
                case DiagramType.SEARCHES:
                    this.channelState.setSelectedChannel(selectedChannel);
                    selectorTableStore.searchterm = '';
                    selectorTableStore.useExactSearching = false;
                    selectorTableStore.tableSorting = {
                        attribute: SearchtermKPI.SearchCount,
                        sortDirection: TableSortDirection.DESC,
                    };

                    setAllowDateCompareRangeWithoutReload(true);
                    lineDiagramStore.termColorIndexes = [];
                    lineDiagramStore.diagramData = [];
                    lineDiagramStore.loadedDiagramData = [];
                    lineDiagramStore.requestedNormalDiagrams = [];
                    lineDiagramStore.updateTermColorIndexes(selectedChannel.name, SearchtermKPI.SearchCount, selectedChannel.name);
                    lineDiagramStore.requestedChannelDiagrams = [
                        {
                            channelName: selectedChannel.name,
                            kpi: SearchtermKPI.SearchCount,
                        },
                    ];
                    setMainDateRangeWithoutReload(mainDateRange);
                    this.mainStore.selectedDateRangeChanged(true);
                    break;
                case DiagramType.SALES:
                    this.channelState.setSelectedChannel(selectedChannel);
                    selectorTableStore.searchterm = '';
                    selectorTableStore.useExactSearching = false;
                    selectorTableStore.tableSorting = {
                        sortDirection: TableSortDirection.DESC,
                        attribute: SearchtermKPI.SalesFromSearches,
                    };

                    setAllowDateCompareRangeWithoutReload(true);
                    lineDiagramStore.termColorIndexes = [];
                    lineDiagramStore.diagramData = [];
                    lineDiagramStore.loadedDiagramData = [];
                    lineDiagramStore.requestedNormalDiagrams = [];
                    lineDiagramStore.updateTermColorIndexes(selectedChannel.name, SearchtermKPI.SalesFromSearches, selectedChannel.name);
                    lineDiagramStore.requestedChannelDiagrams = [
                        {
                            channelName: selectedChannel.name,
                            kpi: SearchtermKPI.SalesFromSearches,
                        },
                    ];
                    setMainDateRangeWithoutReload(mainDateRange);
                    this.mainStore.selectedDateRangeChanged(true);
                    break;
                case DiagramType.HOT_TEN_SEARCHTERMS:
                    this.hotFlopDiagramTitleClicked(SearchtermSlopeOutlierKPI.MinSearchSlopeValue, TableSortDirection.ASC);
                    break;
                case DiagramType.FLOP_TEN_SEARCHTERMS:
                    this.hotFlopDiagramTitleClicked(SearchtermSlopeOutlierKPI.MaxSearchSlopeValue, TableSortDirection.DESC);
                    break;
                default:
                    throw new Error('Invalid diagram ' + diagram);
            }
        });
    }

    private hotFlopDiagramTitleClicked(sortedColumn: KPI, sortDirection: TableSortDirection, searchterm = '') {
        const {selectedChannel} = this.channelState;
        if (!selectedChannel) {
            return;
        }

        const {selectorTableStore, lineDiagramStore} = this.mainStore;
        const {setMainDateRangeWithoutReload, setAllowDateCompareRangeWithoutReload, mainDateRange} = this.timeState;
        this.mainStore.activeAnalyticsTab = AnalyticsTab.SearchView;

        transaction(() => {
            this.channelState.setSelectedChannel(selectedChannel);
            selectorTableStore.searchterm = searchterm;
            selectorTableStore.useExactSearching = true;
            selectorTableStore.tableSorting = {
                attribute: sortedColumn,
                sortDirection,
            };

            setAllowDateCompareRangeWithoutReload(false);
            lineDiagramStore.termColorIndexes = [];
            lineDiagramStore.diagramData = [];
            lineDiagramStore.loadedDiagramData = [];
            lineDiagramStore.requestedNormalDiagrams = [];
            lineDiagramStore.requestedChannelDiagrams = [];
            setMainDateRangeWithoutReload(mainDateRange);
            this.mainStore.selectedDateRangeChanged(true);
        });
    }

    public flopTenBarClick = (diagramData: BarChartData): void => {
        this.mainStore.activeAnalyticsTab = AnalyticsTab.SearchView;
        this.hotFlopDiagramTitleClicked(SearchtermSlopeOutlierKPI.MaxSearchSlopeValue, TableSortDirection.DESC, diagramData.name);
    };

    public hotTenBarClick = (diagramData: BarChartData): void => {
        this.mainStore.activeAnalyticsTab = AnalyticsTab.SearchView;
        this.hotFlopDiagramTitleClicked(SearchtermSlopeOutlierKPI.MinSearchSlopeValue, TableSortDirection.ASC, diagramData.name);
    };

    public salesLabelClicked = (diagramData: BarChartData): void => {
        this.labelClicked(diagramData, SearchtermKPI.SalesFromSearches);
    };

    public searchLabelClicked = (diagramData: BarChartData): void => {
        this.labelClicked(diagramData, SearchtermKPI.SearchCount);
    };

    private labelClicked(diagramData: BarChartData, kpi: KPI) {
        const {selectedChannel} = this.channelState;
        if (!selectedChannel) {
            return;
        }

        const {date: momentDate} = diagramData;

        this.mainStore.activeAnalyticsTab = AnalyticsTab.SearchView;
        const selectorTableStore = this.mainStore.selectorTableStore;
        const {timeIntervalStore} = this.mainStore;
        const lineDiagramStore = this.mainStore.lineDiagramStore;

        transaction(() => {
            selectorTableStore.searchterm = '';
            selectorTableStore.useExactSearching = false;
            selectorTableStore.tableSorting = {
                sortDirection: TableSortDirection.DESC,
                attribute: kpi,
            };

            timeIntervalStore.allowDateCompareRange = false;
            lineDiagramStore.termColorIndexes = [];
            lineDiagramStore.diagramData = [];
            lineDiagramStore.loadedDiagramData = [];
            lineDiagramStore.requestedNormalDiagrams = [];
            lineDiagramStore.updateTermColorIndexes(selectedChannel.name, kpi, selectedChannel.name);
            lineDiagramStore.requestedChannelDiagrams = [
                {
                    channelName: selectedChannel.name,
                    kpi,
                },
            ];
            this.timeState.setMainDateRangeWithoutReload({startDate: moment(momentDate), endDate: moment(momentDate)});
            this.mainStore.selectedDateRangeChanged(true);
        });
    }

    public generateKpiDataPackageFiller(
        groupName: KPI,
        value: number,
        selectedChannel: Channel,
        isCompareDiagram = false,
        additionalInformation = {}
    ): KpiDataPackage {
        return {
            searchtermOrProductId: '',
            additionalInformation,
            kpi: groupName,
            isChannelDiagram: true,
            isCompareDiagram,
            value,
            channel: selectedChannel,
            relValue: 0,
        };
    }

    public titleClickedLongtailGraphes(kpi: KPI, searchterm?: string) {
        this.mainStore.activeAnalyticsTab = AnalyticsTab.SearchView;
        const {timeIntervalStore, lineDiagramStore, selectorTableStore} = this.mainStore;
        const {setMainDateRangeWithoutReload, setAllowDateCompareRangeWithoutReload} = this.timeState;
        const {mainDateRange} = timeIntervalStore;

        transaction(() => {
            selectorTableStore.searchterm = searchterm ? searchterm : '';
            selectorTableStore.useExactSearching = true;
            selectorTableStore.tableSorting = {
                sortDirection: TableSortDirection.DESC,
                attribute: kpi,
            };

            lineDiagramStore.termColorIndexes = [];
            lineDiagramStore.diagramData = [];
            lineDiagramStore.loadedDiagramData = [];
            lineDiagramStore.requestedNormalDiagrams = [];
            lineDiagramStore.requestedChannelDiagrams = [];
            setAllowDateCompareRangeWithoutReload(false);
            setMainDateRangeWithoutReload(mainDateRange);
            this.mainStore.selectedDateRangeChanged(true);
        });
    }

    public generateKpiPackageFiller(groupName: KPI, isCompareDiagram = false): KpiPackage {
        const {selectedChannel} = this.channelState;
        if (!selectedChannel) {
            throw new Error('selectedChannel is undefined');
        }
        return {
            channel: selectedChannel,
            kpi: groupName,
            searchtermOrProductId: '',
            isChannelDiagram: true,
            isCompareDiagram,
        };
    }

    public graphClickedLongtailGraphes = (payload: BarChartData) => {
        const dataEntry = payload.data[0];
        this.titleClickedLongtailGraphes(dataEntry.kpi, dataEntry.additionalInformation && dataEntry.additionalInformation.searchterm);
    };

    public getDashboardDataForCurrentChannel(): DashboardData {
        const {selectedChannel} = this.mainStore.channelStore;
        return this.dashboardData && selectedChannel && this.dashboardData.has(selectedChannel.name)
            ? this.getDashboardData(selectedChannel.name)
            : this.getLoadingDashbordDataStructure();
    }
}

export default DashboardStore;



// WEBPACK FOOTER //
// ./src/stores/DashboardStore.ts